import {
  CUSTOMER_PORTAL_DEFAULTS,
  type CustomerPortalInputs
} from "../calculators/customerPortal/defaults";
import {
  ECOMMERCE_DEFAULTS,
  type EcommerceInputs
} from "../calculators/ecommerce/defaults";
import {
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  convertCurrencyToUsd,
  convertUsdToCurrency,
  currencyFromCountry,
  defaultCurrencyFromLocale,
  setUsdToCurrencyRates
} from "../lib/currency";
import { resolvePartnerBrand } from "../lib/brands";
import type { CurrencyCode } from "../lib/types";
import {
  sanitizeCurrencyInput,
  sanitizeNumberInput,
  sanitizePercentInput
} from "../lib/parse";
import { renderCustomerPortalPage } from "./pages/customerPortalPage";
import { renderEcommercePage } from "./pages/ecommercePage";
import { resolveCalculatorRoute, type CalculatorRoute } from "./router";

interface MountRuntimeOptions {
  navigatorLanguage?: string;
  fetchImpl?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

interface FocusState {
  name: string;
  selectionStart: number | null;
  selectionEnd: number | null;
}

function parseInputValue(
  rawValue: string,
  kind: string,
  fallback: number,
  currency: CurrencyCode
): number {
  if (kind === "currency") {
    const fallbackDisplay = convertUsdToCurrency(fallback, currency);
    const parsedDisplay = sanitizeCurrencyInput(rawValue, fallbackDisplay);

    return convertCurrencyToUsd(parsedDisplay, currency);
  }

  if (kind === "percent") {
    return sanitizePercentInput(rawValue, fallback);
  }

  return sanitizeNumberInput(rawValue, fallback);
}

function asCurrencyCode(value: string): CurrencyCode {
  if (SUPPORTED_CURRENCIES.includes(value as CurrencyCode)) {
    return value as CurrencyCode;
  }

  return DEFAULT_CURRENCY;
}

function resolveNavigatorLanguage(runtime: MountRuntimeOptions): string {
  if (runtime.navigatorLanguage) {
    return runtime.navigatorLanguage;
  }

  return typeof navigator !== "undefined" ? navigator.language : "";
}

function currencyFromGeoPayload(payload: unknown): CurrencyCode | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const data = payload as { country?: unknown };

  if (typeof data.country !== "string") {
    return null;
  }

  return currencyFromCountry(data.country);
}

function ratesFromFxPayload(
  payload: unknown
): Partial<Record<CurrencyCode, number>> | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const data = payload as { rates?: unknown };

  if (!data.rates || typeof data.rates !== "object") {
    return null;
  }

  const rawRates = data.rates as Record<string, unknown>;
  const rates: Partial<Record<CurrencyCode, number>> = {};

  for (const currency of SUPPORTED_CURRENCIES) {
    const value = rawRates[currency];

    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
      continue;
    }

    rates[currency] = value;
  }

  return Object.keys(rates).length > 0 ? rates : null;
}

export function mountRoiApp(
  root: HTMLElement,
  locationLike: Location = window.location,
  runtime: MountRuntimeOptions = {}
): void {
  const route: CalculatorRoute = resolveCalculatorRoute(locationLike.pathname);
  const params = new URLSearchParams(locationLike.search);
  const brand = resolvePartnerBrand(params.get("partner") ?? params.get("logo"));

  const fetchImpl =
    runtime.fetchImpl ??
    (typeof fetch === "function" ? fetch.bind(globalThis) : undefined);

  let currency: CurrencyCode = defaultCurrencyFromLocale(resolveNavigatorLanguage(runtime));
  let currencyTouched = false;
  let helpOpen = false;
  let hasRenderedOnce = false;
  let customerInputs: CustomerPortalInputs = { ...CUSTOMER_PORTAL_DEFAULTS };
  let ecommerceInputs: EcommerceInputs = { ...ECOMMERCE_DEFAULTS };

  const applyTheme = () => {
    root.style.setProperty("--brand-primary", brand.primaryColor);
    root.style.setProperty("--brand-accent", brand.accentColor);
    root.style.setProperty("--brand-ink", brand.inkColor);
    root.style.setProperty("--brand-soft", brand.softColor);
  };

  const restoreFocus = (focusState?: FocusState) => {
    if (!focusState) {
      return;
    }

    const input = root.querySelector<HTMLInputElement>(
      `input[name='${focusState.name}']`
    );

    if (!input) {
      return;
    }

    input.focus();

    const length = input.value.length;
    const start = Math.min(Math.max(focusState.selectionStart ?? length, 0), length);
    const end = Math.min(Math.max(focusState.selectionEnd ?? start, start), length);

    try {
      input.setSelectionRange(start, end);
    } catch {
      // Ignore selection restoration failures for non-text-like inputs.
    }
  };

  const handleFieldInput = (event: Event) => {
    const target = event.currentTarget as HTMLInputElement;
    const kind = target.dataset.kind ?? "number";
    const focusState: FocusState = {
      name: target.name,
      selectionStart: target.selectionStart,
      selectionEnd: target.selectionEnd
    };

    if (route === "customer-portal") {
      const key = target.name as keyof CustomerPortalInputs;
      const fallback = customerInputs[key];

      customerInputs = {
        ...customerInputs,
        [key]: parseInputValue(target.value, kind, fallback, currency)
      };
    } else {
      const key = target.name as keyof EcommerceInputs;
      const fallback = ecommerceInputs[key];

      ecommerceInputs = {
        ...ecommerceInputs,
        [key]: parseInputValue(target.value, kind, fallback, currency)
      };
    }

    render(focusState);
  };

  const handleCurrencyChange = (event: Event) => {
    const target = event.currentTarget as HTMLSelectElement;
    currencyTouched = true;
    currency = asCurrencyCode(target.value);
    render();
  };

  const openHelp = () => {
    helpOpen = true;
    render();
  };

  const closeHelp = () => {
    helpOpen = false;
    render();
  };

  const bindEvents = () => {
    root.querySelectorAll<HTMLInputElement>("input[data-field='true']").forEach((input) => {
      input.addEventListener("input", handleFieldInput);
    });

    const currencySelect = root.querySelector<HTMLSelectElement>(
      "select[data-action='currency-change']"
    );

    if (currencySelect) {
      currencySelect.addEventListener("change", handleCurrencyChange);
    }

    const openButton = root.querySelector<HTMLButtonElement>("button[data-action='open-help']");
    const closeButton = root.querySelector<HTMLButtonElement>("button[data-action='close-help']");
    const modal = root.querySelector<HTMLDivElement>(".help-modal");

    if (openButton) {
      openButton.addEventListener("click", openHelp);
    }

    if (closeButton) {
      closeButton.addEventListener("click", closeHelp);
    }

    if (modal) {
      modal.addEventListener("click", (event) => {
        if (event.target === modal) {
          closeHelp();
        }
      });
    }
  };

  const render = (focusState?: FocusState) => {
    const animateEntrance = !hasRenderedOnce;

    root.innerHTML =
      route === "customer-portal"
        ? renderCustomerPortalPage({
            inputs: customerInputs,
            currency,
            brand,
            helpOpen,
            animateEntrance
          })
        : renderEcommercePage({
            inputs: ecommerceInputs,
            currency,
            brand,
            helpOpen,
            animateEntrance
          });

    applyTheme();
    bindEvents();
    restoreFocus(focusState);
    hasRenderedOnce = true;
  };

  const hydrateCurrencyFromGeo = async () => {
    if (!fetchImpl || currencyTouched) {
      return;
    }

    try {
      const response = await fetchImpl("/api/geo", {
        headers: {
          accept: "application/json"
        }
      });

      if (!response.ok) {
        return;
      }

      const detected = currencyFromGeoPayload(await response.json());

      if (!detected || currencyTouched || detected === currency) {
        return;
      }

      currency = detected;
      render();
    } catch {
      // Network or runtime issues should not block calculator interaction.
    }
  };

  const hydrateExchangeRates = async () => {
    if (!fetchImpl) {
      return;
    }

    try {
      const response = await fetchImpl("/api/fx", {
        headers: {
          accept: "application/json"
        }
      });

      if (!response.ok) {
        return;
      }

      const parsedRates = ratesFromFxPayload(await response.json());

      if (!parsedRates) {
        return;
      }

      if (!setUsdToCurrencyRates(parsedRates)) {
        return;
      }

      render();
    } catch {
      // FX feed availability should not block calculator interaction.
    }
  };

  render();
  void hydrateCurrencyFromGeo();
  void hydrateExchangeRates();
}
